import type { AssertNever } from "@redotech/util/type";
import { type AnalyticsTimeUnit } from "./analytics-query-constants";
import {
  ANALYTICS_FUNCTION_NAMES,
  AnalyticsFunctionRegistry,
  analyticsFunctionSignature,
  isAnalyticsBooleanPredicate,
} from "./analytics-query-function-registry";
import type {
  AnalyticsBooleanPredicateName,
  AnalyticsFunctionName,
} from "./analytics-query-function-types";

/** Widens a registry row to the signature contract so optional slots are addressable. */
type AnalyticsFunctionRow<Fn extends AnalyticsFunctionName> =
  (typeof AnalyticsFunctionRegistry)[Fn];

type CallParams<Fn extends AnalyticsFunctionName> =
  AnalyticsFunctionRow<Fn>["params"];

type CallVariadic<Fn extends AnalyticsFunctionName> =
  AnalyticsFunctionRow<Fn> extends { readonly variadic: true } ? true : false;

type CallCount<Fn extends AnalyticsFunctionName> = CallParams<Fn>["length"];

type CallUnit<Fn extends AnalyticsFunctionName> =
  AnalyticsFunctionRow<Fn> extends {
    readonly units: readonly AnalyticsTimeUnit[];
  }
    ? "day"
    : AnalyticsFunctionRow<Fn> extends { readonly unit: "required" }
      ? "any"
      : "none";

interface BooleanTypedRule {
  readonly kind: "typed";
  readonly types: readonly ["boolean"];
}

type CallBooleanArgs<Params> = Params extends readonly []
  ? false
  : Params extends readonly [{ readonly rule: BooleanTypedRule }, ...infer Rest]
    ? Rest extends readonly []
      ? true
      : CallBooleanArgs<Rest>
    : false;

type CallShape<
  Fn extends AnalyticsFunctionName,
  Variadic extends boolean,
  Count extends number,
  Unit extends "none" | "day" | "any",
  BooleanArgs extends boolean,
> =
  CallVariadic<Fn> extends Variadic
    ? CallCount<Fn> extends Count
      ? CallUnit<Fn> extends Unit
        ? CallBooleanArgs<CallParams<Fn>> extends BooleanArgs
          ? Fn
          : never
        : never
      : never
    : never;

type CallNames<
  Variadic extends boolean,
  Count extends number,
  Unit extends "none" | "day" | "any",
  BooleanArgs extends boolean,
> = {
  [Fn in AnalyticsFunctionName]: CallShape<
    Fn,
    Variadic,
    Count,
    Unit,
    BooleanArgs
  >;
}[AnalyticsFunctionName];

export type AnalyticsNullaryFunctionName = CallNames<false, 0, "none", false>;
export type AnalyticsUnaryFunctionName = CallNames<false, 1, "none", false>;
export type AnalyticsUnaryBooleanFunctionName = CallNames<
  false,
  1,
  "none",
  true
>;
export type AnalyticsBinaryFunctionName = CallNames<false, 2, "none", false>;
export type AnalyticsTernaryFunctionName = CallNames<false, 3, "none", false>;
export type AnalyticsQuinaryFunctionName = CallNames<false, 5, "none", false>;
export type AnalyticsVariadicFunctionName = CallNames<true, 1, "none", false>;
export type AnalyticsVariadicPairFunctionName = CallNames<
  true,
  2,
  "none",
  false
>;
export type AnalyticsVariadicBooleanFunctionName = CallNames<
  true,
  1,
  "none",
  true
>;
export type AnalyticsDayUnaryFunctionName = CallNames<false, 1, "day", false>;
export type AnalyticsAnyUnitUnaryFunctionName = CallNames<
  false,
  1,
  "any",
  false
>;
export type AnalyticsAnyUnitBinaryFunctionName = CallNames<
  false,
  2,
  "any",
  false
>;

type AnalyticsCallShapeName =
  | AnalyticsNullaryFunctionName
  | AnalyticsUnaryFunctionName
  | AnalyticsUnaryBooleanFunctionName
  | AnalyticsBinaryFunctionName
  | AnalyticsTernaryFunctionName
  | AnalyticsQuinaryFunctionName
  | AnalyticsVariadicFunctionName
  | AnalyticsVariadicPairFunctionName
  | AnalyticsVariadicBooleanFunctionName
  | AnalyticsDayUnaryFunctionName
  | AnalyticsAnyUnitUnaryFunctionName
  | AnalyticsAnyUnitBinaryFunctionName;

type AnalyticsCallShapeTag<Fn> =
  | (Fn extends AnalyticsNullaryFunctionName ? "nullary" : never)
  | (Fn extends AnalyticsUnaryFunctionName ? "unary" : never)
  | (Fn extends AnalyticsUnaryBooleanFunctionName ? "unary-boolean" : never)
  | (Fn extends AnalyticsBinaryFunctionName ? "binary" : never)
  | (Fn extends AnalyticsTernaryFunctionName ? "ternary" : never)
  | (Fn extends AnalyticsQuinaryFunctionName ? "quinary" : never)
  | (Fn extends AnalyticsVariadicFunctionName ? "variadic" : never)
  | (Fn extends AnalyticsVariadicPairFunctionName ? "variadic-pair" : never)
  | (Fn extends AnalyticsVariadicBooleanFunctionName
      ? "variadic-boolean"
      : never)
  | (Fn extends AnalyticsDayUnaryFunctionName ? "day" : never)
  | (Fn extends AnalyticsAnyUnitUnaryFunctionName ? "any-unit-unary" : never)
  | (Fn extends AnalyticsAnyUnitBinaryFunctionName ? "any-unit-binary" : never);

type AnalyticsCallShapeIsUnion<T, Copy = T> = T extends unknown
  ? [Copy] extends [T]
    ? false
    : true
  : never;

/** A function name missing from every call shape. */
export type AnalyticsCallShapesAreGrouped = AssertNever<
  Exclude<AnalyticsFunctionName, AnalyticsCallShapeName>
>;

/** A function name in two call shapes. `fn` cannot discriminate it. */
export type AnalyticsCallShapesAreExclusive = AssertNever<
  {
    [Fn in AnalyticsFunctionName]: AnalyticsCallShapeIsUnion<
      AnalyticsCallShapeTag<Fn>
    > extends true
      ? Fn
      : never;
  }[AnalyticsFunctionName]
>;

const callShape = (
  fn: AnalyticsFunctionName,
  variadic: boolean,
  count: number,
  unit: "none" | "day" | "any",
  booleanArguments: boolean,
): boolean => {
  const row = analyticsFunctionSignature(fn);
  const booleanArgs =
    row.params.length > 0 &&
    row.params.every((param) => {
      const rule = param.rule;
      return (
        rule.kind === "typed" &&
        rule.types.length === 1 &&
        rule.types[0] === "boolean"
      );
    });
  const rowUnit =
    row.units !== undefined ? "day" : row.unit === "required" ? "any" : "none";
  return (
    (row.variadic === true) === variadic &&
    row.params.length === count &&
    rowUnit === unit &&
    booleanArgs === booleanArguments
  );
};

const callNames = <Name extends AnalyticsFunctionName>(
  variadic: boolean,
  count: number,
  unit: "none" | "day" | "any",
  booleanArguments: boolean,
): readonly Name[] =>
  ANALYTICS_FUNCTION_NAMES.filter((fn): fn is Name =>
    callShape(fn, variadic, count, unit, booleanArguments),
  );

export const ANALYTICS_NULLARY_FUNCTION_NAMES: readonly AnalyticsNullaryFunctionName[] =
  callNames(false, 0, "none", false);
export const ANALYTICS_UNARY_FUNCTION_NAMES: readonly AnalyticsUnaryFunctionName[] =
  callNames(false, 1, "none", false);
export const ANALYTICS_UNARY_BOOLEAN_FUNCTION_NAMES: readonly AnalyticsUnaryBooleanFunctionName[] =
  callNames(false, 1, "none", true);
export const ANALYTICS_BINARY_FUNCTION_NAMES: readonly AnalyticsBinaryFunctionName[] =
  callNames(false, 2, "none", false);
export const ANALYTICS_TERNARY_FUNCTION_NAMES: readonly AnalyticsTernaryFunctionName[] =
  callNames(false, 3, "none", false);
export const ANALYTICS_QUINARY_FUNCTION_NAMES: readonly AnalyticsQuinaryFunctionName[] =
  callNames(false, 5, "none", false);
export const ANALYTICS_VARIADIC_FUNCTION_NAMES: readonly AnalyticsVariadicFunctionName[] =
  callNames(true, 1, "none", false);
export const ANALYTICS_VARIADIC_PAIR_FUNCTION_NAMES: readonly AnalyticsVariadicPairFunctionName[] =
  callNames(true, 2, "none", false);
export const ANALYTICS_VARIADIC_BOOLEAN_FUNCTION_NAMES: readonly AnalyticsVariadicBooleanFunctionName[] =
  callNames(true, 1, "none", true);
export const ANALYTICS_DAY_UNARY_FUNCTION_NAMES: readonly AnalyticsDayUnaryFunctionName[] =
  callNames(false, 1, "day", false);
export const ANALYTICS_ANY_UNIT_UNARY_FUNCTION_NAMES: readonly AnalyticsAnyUnitUnaryFunctionName[] =
  callNames(false, 1, "any", false);
export const ANALYTICS_ANY_UNIT_BINARY_FUNCTION_NAMES: readonly AnalyticsAnyUnitBinaryFunctionName[] =
  callNames(false, 2, "any", false);

const predicatesIn = <Name extends AnalyticsBooleanPredicateName>(
  names: readonly AnalyticsFunctionName[],
): readonly Name[] =>
  names.filter((fn): fn is Name => isAnalyticsBooleanPredicate(fn));

export const ANALYTICS_UNARY_PREDICATE_NAMES: readonly (AnalyticsBooleanPredicateName &
  AnalyticsUnaryFunctionName)[] = predicatesIn(ANALYTICS_UNARY_FUNCTION_NAMES);
export const ANALYTICS_BINARY_PREDICATE_NAMES: readonly (AnalyticsBooleanPredicateName &
  AnalyticsBinaryFunctionName)[] = predicatesIn(
  ANALYTICS_BINARY_FUNCTION_NAMES,
);
export const ANALYTICS_TERNARY_PREDICATE_NAMES: readonly (AnalyticsBooleanPredicateName &
  AnalyticsTernaryFunctionName)[] = predicatesIn(
  ANALYTICS_TERNARY_FUNCTION_NAMES,
);
export const ANALYTICS_VARIADIC_PAIR_PREDICATE_NAMES: readonly (AnalyticsBooleanPredicateName &
  AnalyticsVariadicPairFunctionName)[] = predicatesIn(
  ANALYTICS_VARIADIC_PAIR_FUNCTION_NAMES,
);
